import { countryLanguages, defaultLanguage, LocaleDetails } from 'shared/src/types/languages.type';
import { deadUrlToQuery } from './url-words';

/**
 * What a not-found boundary can recover about the URL that failed.
 *
 * Next hands a not-found.tsx neither params nor searchParams, so the pathname
 * comes from the `x-inception-pathname` request header the middleware sets for
 * every page request (the root layout reads the same header for <html lang>).
 * Pure so it unit-tests: the boundary passes the header value in.
 */
export type DeadPath = {
  locale: LocaleDetails;
  /** True when the first segment was a locale we serve. */
  localeKnown: boolean;
  /** Path segments after the locale, still URL-encoded. */
  categories: string[];
  /**
   * The deepest word segment as human words (the visitor's probable query), or
   * '' when the path holds none: route words, ids and file names are never a
   * query (see ./url-words).
   */
  query: string;
};

export const parseDeadPath = (pathname: string | null | undefined): DeadPath => {
  const segments = (pathname ?? '').split('/').filter(Boolean);
  const first = segments[0];
  const known = first ? countryLanguages[first] : undefined;
  const categories = known ? segments.slice(1) : segments;
  return {
    locale: known ?? defaultLanguage,
    localeKnown: Boolean(known),
    categories,
    query: deadUrlToQuery(categories),
  };
};
